import { LoadingRows } from "../../UI/Loading/LoadingRows.tsx";
import { useMemo, useState } from "react";
import { ErrorFeedback } from "../../UI/Feedback/ErrorFeedback.tsx";
import { InfoFeedback } from "../../UI/Feedback/InfoFeedback.tsx";
import { useUserClassRoomHistory } from "./useUserClassRoomHistory.ts";
import { UserClassRoomHistoryCard } from "./UserClassRoomHistoryCard.tsx";

type LessonTypeFilter = "all" | "oneOnOne" | "group";
type ClassRoomKindFilter = "all" | "lessons" | "progressChecks";

function FilterToggle({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        "px-3 py-1.5 rounded-full text-sm font-medium border transition-colors",
        active
          ? "bg-slate-800 text-white border-slate-800"
          : "bg-white text-slate-600 border-slate-300 hover:border-slate-500",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

export function UserClassRoomHistoryComponent({
  userId,
  userFullName,
}: {
  userId: string;
  userFullName?: string;
}) {
  const [lessonType, setLessonType] = useState<LessonTypeFilter>("all");
  const [kind, setKind] = useState<ClassRoomKindFilter>("all");
  const [includeUpcoming, setIncludeUpcoming] = useState(false);
  const { classRooms, loading, error } = useUserClassRoomHistory(userId, {
    includeUpcoming,
  });

  const visibleClassRooms = useMemo(() => {
    let res = classRooms;
    if (lessonType === "oneOnOne") res = res.filter((c) => c.is1on1);
    if (lessonType === "group") res = res.filter((c) => !c.is1on1);
    if (kind === "lessons") res = res.filter((c) => !c.isProgressCheck);
    if (kind === "progressChecks") res = res.filter((c) => c.isProgressCheck);
    return res;
  }, [classRooms, lessonType, kind]);

  const giftedCount = useMemo(
    () => visibleClassRooms.filter((c) => c.giftedByPromo).length,
    [visibleClassRooms],
  );

  const progressCheckCount = useMemo(
    () => visibleClassRooms.filter((c) => c.isProgressCheck).length,
    [visibleClassRooms],
  );

  if (loading && classRooms.length === 0) return <LoadingRows rows={4} />;
  if (error) return <ErrorFeedback title={"Error"} message={error.message} />;

  return (
    <div className={"space-y-4"}>
      <div className={"flex flex-wrap items-center gap-2"}>
        <span className={"text-sm text-slate-500 font-medium"}>Type:</span>
        <FilterToggle
          label={"All"}
          active={kind === "all"}
          onClick={() => setKind("all")}
        />
        <FilterToggle
          label={"Lessons"}
          active={kind === "lessons"}
          onClick={() => setKind("lessons")}
        />
        <FilterToggle
          label={"Progress checks"}
          active={kind === "progressChecks"}
          onClick={() => setKind("progressChecks")}
        />
      </div>

      <div className={"flex flex-wrap items-center gap-2"}>
        <span className={"text-sm text-slate-500 font-medium"}>Format:</span>
        <FilterToggle
          label={"All"}
          active={lessonType === "all"}
          onClick={() => setLessonType("all")}
        />
        <FilterToggle
          label={"1 to 1"}
          active={lessonType === "oneOnOne"}
          onClick={() => setLessonType("oneOnOne")}
        />
        <FilterToggle
          label={"Group"}
          active={lessonType === "group"}
          onClick={() => setLessonType("group")}
        />
        <FilterToggle
          label={"Include upcoming"}
          active={includeUpcoming}
          onClick={() => setIncludeUpcoming((v) => !v)}
        />
      </div>

      <p className={"text-sm text-slate-500"}>
        {visibleClassRooms.length}{" "}
        {visibleClassRooms.length === 1 ? "class" : "classes"}
        {progressCheckCount > 0 && <> · {progressCheckCount} progress check</>}
        {giftedCount > 0 && <> · {giftedCount} gifted by a promo</>}
      </p>

      {visibleClassRooms.length === 0 ? (
        <InfoFeedback
          title={"No classes"}
          message={`${userFullName ?? "This user"} has not attended any class matching these filters.`}
        />
      ) : (
        <div className={"space-y-3"}>
          {visibleClassRooms.map((classRoom) => (
            <UserClassRoomHistoryCard
              key={classRoom.classRoomId}
              classRoom={classRoom}
            />
          ))}
        </div>
      )}
    </div>
  );
}
